// pages/Settings.tsx
import React, { useEffect, useState } from "react";
import { Settings as SettingsIcon } from "lucide-react";
import Cookies from "js-cookie";

// Import child components
import PaymentMethods from "../components/settings/PaymentMethods";
import DangerZone from "../components/settings/DangerZone";
import DeleteAccountModal from "../components/settings/DeleteAccountModal";
import AddPaymentMethodModal from "../components/settings/AddPaymentMethodModal";
import SettingsSkeleton from "../components/settings/SettingsSkeleton";

// Import hooks and types
import {
  useDeleteAccount,
  useFetchUserDetails,
} from "../lib/api/authOnboarding";
import { getDecodedJwt } from "../lib/auth";
import { IPaymentMethod } from "../lib/types/authOnboarding";
import {
  useAddPaymentMethod,
  useDeletePaymentMethod,
  useSetDefaultPaymentMethod,
} from "../lib/api/settings";
import { AddPaymentMethodPayload } from "../lib/types/settings";
import { useAuth } from "../context/AuthContext";

const Settings: React.FC = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);
  const user = getDecodedJwt();
  const { logout } = useAuth();
  const { data: userDetails, isLoading } = useFetchUserDetails(user?.id);

  const { mutateAsync: addPaymentMethod, isPending: isAddingPayment } =
    useAddPaymentMethod();
  const { mutateAsync: setDefaultPayment, isPending: isSettingDefault } =
    useSetDefaultPaymentMethod();
  const { mutateAsync: deletePayment, isPending: isDeletingPayment } =
    useDeletePaymentMethod();
  const { mutateAsync: deleteAccount, isPending: isDeletingAccount } =
    useDeleteAccount();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);

  // Get payment methods from user details
  const paymentMethods: IPaymentMethod[] = userDetails?.paymentMethods || [];

  const handleSetDefaultPayment = async (paymentMethodId: string) => {
    try {
      await setDefaultPayment({
        id: user?.id as string,
        paymentMethodId,
      });
    } catch (error) {
      console.error("Error setting default payment:", error);
    }
  };

  const handleDeletePaymentMethod = async (paymentMethodId: string) => {
    try {
      await deletePayment({
        id: user?.id as string,
        paymentMethodId,
      });
    } catch (error) {
      console.error("Error deleting payment method:", error);
    }
  };

  const handleAddPaymentMethod = async (
    payload: AddPaymentMethodPayload
  ): Promise<boolean> => {
    try {
      await addPaymentMethod({
        id: user?.id as string,
        payload,
      });
      setShowAddPaymentModal(false);
      return true;
    } catch (error) {
      console.error("Error adding payment method:", error);
      return false;
    }
  };

  const handleDeleteAccount = async (
    reason: string,
    feedback: string
  ): Promise<boolean> => {
    try {
      await deleteAccount({
        id: user?.id as string,
        payload: { reason, feedback },
      });

      // Clear auth data
      logout();
      Cookies.remove("authToken");
      localStorage.removeItem("user");

      // Redirect to home page
      window.location.href = "/";
      return true;
    } catch (error) {
      console.error("Error deleting account:", error);
      return false;
    }
  };

  if (isLoading) {
    return <SettingsSkeleton />;
  }

  const existingPaymentTypes = paymentMethods.map((pm) => pm.type);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-200 to-gray-400 flex items-center justify-center">
              <SettingsIcon className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">
              Settings
            </h1>
          </div>
          <p className="text-gray-500 mt-1">
            Manage your payment methods and account preferences
          </p>
        </div>

        {/* Main Content */}
        <div className="space-y-6">
          {/* Payment Methods Section */}
          <PaymentMethods
            paymentMethods={paymentMethods}
            onAddNew={() => setShowAddPaymentModal(true)}
            onSetDefault={handleSetDefaultPayment}
            onDelete={handleDeletePaymentMethod}
            isSettingDefault={isSettingDefault}
            isDeletingPayment={isDeletingPayment}
          />

          {/* Danger Zone Section */}
          <DangerZone onDeleteAccount={() => setShowDeleteModal(true)} />
        </div>
      </div>

      {/* Modals */}
      {showDeleteModal && (
        <DeleteAccountModal
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteAccount}
          isPending={isDeletingAccount}
        />
      )}

      {showAddPaymentModal && (
        <AddPaymentMethodModal
          onClose={() => setShowAddPaymentModal(false)}
          onAdd={handleAddPaymentMethod}
          isPending={isAddingPayment}
          existingTypes={existingPaymentTypes}
        />
      )}
    </div>
  );
};

export default Settings;
